const translations = {
  // Header
  header: {
    en: {
      solutions: 'Solutions',
      features: 'Features',
      contact: 'Contact',
      getStarted: 'Get Started'
    },
    de: {
      solutions: 'Lösungen',
      features: 'Funktionen',
      contact: 'Kontakt',
      getStarted: 'Loslegen'
    }
  },
  
  // Hero
  hero: {
    en: {
      title: 'Making AI Transparent and Understandable',
      subtitle: 'Our platform delivers data protection-compliant, comprehensible AI solutions. No more black box – but clear explanations that build trust and enable effective collaboration between humans and AI.',
      getStarted: 'Contact',
      watchDemo: 'Watch Demo',
      trustedBy: 'Trusted by industry leaders'
    },
    de: {
      title: 'Making AI Transparent and Understandable',
      subtitle: 'Unsere Plattform liefert datenschutzkonforme, nachvollziehbare KI-Lösungen. Keine Blackbox mehr – sondern klare Erklärungen, die Vertrauen schaffen und eine effektive Zusammenarbeit zwischen Mensch und KI ermöglichen.',
      getStarted: 'Kontakt',
      watchDemo: 'Demo ansehen',
      trustedBy: 'Vertraut von Branchenführern'
    }
  },
  
  // Features
  features: {
    en: {
      title: 'Powerful Features',
      subtitle: 'Our core system is based on a powerful Retrieval-Augmented-Generation (RAG) platform that can be operated locally or in our secure cloud. With our "experts" and "assistants," we make complex AI processes visible, efficient, and data protection compliant.',
      transparentDecision: {
        title: 'Transparent Decision Making',
        description: 'Say goodbye to black box systems. Our RAG architecture provides you with clear, visual explanations so you can understand every AI decision step by step.'
      },
      comprehensiveAnalytics: {
        title: 'Comprehensive Analytics',
        description: 'Gain deep insights into the performance of your AI models. We provide detailed metrics to continuously monitor and improve quality, accuracy, and prediction quality.'
      },
      biasDetection: {
        title: 'Bias Detection',
        description: 'Identify and reduce algorithmic biases using state-of-the-art analysis methods. Our system suggests concrete measures to ensure your AI remains fair and reliable.'
      },
      continuousLearning: {
        title: 'Continuous Learning',
        description: 'Thanks to continuous adaptation, our platform constantly learns from feedback and new data. This keeps your models up-to-date and powerful.'
      },
      humanAICollaboration: {
        title: 'Human-AI Collaboration',
        description: 'Facilitate collaboration between domain experts and AI. Teams can manage "experts" together or share them with each other via an intuitive user interface, SSO connection, and multi-user support.'
      },
      privacyFocused: {
        title: 'Privacy-Focused',
        description: 'Our solutions meet the highest data protection and security standards. With on-premise options, encrypted data transmission, and GDPR-compliant operation, you are always on the safe side.'
      }
    },
    de: {
      title: 'Leistungsstarke Funktionen',
      subtitle: 'Unser Kernsystem basiert auf einer leistungsstarken Retrieval-Augmented-Generation (RAG)-Plattform, die lokal oder in unserer sicheren Cloud betrieben werden kann. Mit unseren „Experten" und „Assistenten" machen wir komplexe KI-Prozesse sichtbar, effizient und datenschutzkonform.',
      transparentDecision: {
        title: 'Transparent Decision Making',
        description: 'Verabschiede dich von Blackbox-Systemen. Unsere RAG-Architektur liefert dir klare, visuelle Erklärungen, damit du jede KI-Entscheidung Schritt für Schritt nachvollziehen kannst.'
      },
      comprehensiveAnalytics: {
        title: 'Comprehensive Analytics',
        description: 'Gewinne tiefe Einblicke in die Performance deiner KI-Modelle. Wir stellen detaillierte Metriken bereit, um Qualität, Genauigkeit und Vorhersagegüte kontinuierlich zu überwachen und zu verbessern.'
      },
      biasDetection: {
        title: 'Bias Detection',
        description: 'Identifiziere und reduziere algorithmische Verzerrungen mithilfe modernster Analysemethoden. Unser System schlägt dir konkrete Maßnahmen vor, damit deine KI fair und zuverlässig bleibt.'
      },
      continuousLearning: {
        title: 'Continuous Learning',
        description: 'Dank kontinuierlicher Anpassung lernt unsere Plattform laufend aus Feedback und neuen Daten. So bleiben deine Modelle stets aktuell und leistungsfähig.'
      },
      humanAICollaboration: {
        title: 'Human-AI Collaboration',
        description: 'Erleichtere die Zusammenarbeit zwischen Fachexperten und KI. Über eine intuitive Benutzeroberfläche, SSO-Anbindung und Multiuser-Support können Teams gemeinsam „Experten" verwalten oder untereinander freigeben.'
      },
      privacyFocused: {
        title: 'Privacy-Focused',
        description: 'Unsere Lösungen erfüllen höchste Datenschutz- und Sicherheitsstandards. Mit On-Premise-Optionen, verschlüsselter Datenübertragung und DSGVO-konformem Betrieb bist du immer auf der sicheren Seite.'
      }
    }
  },
  
  // Benefits/Solutions
  benefits: {
    en: {
      title: 'Why Choose Explainable AI?',
      subtitle: 'Our platform and services offer real added value - from increased data security to better predictability of your AI deployment.',
      enhancedTransparency: {
        title: 'Enhanced Decision Transparency',
        description: 'Understand the exact decision path of your AI models and build trust among employees, customers, and regulatory authorities.'
      },
      regulatoryCompliance: {
        title: 'Regulatory Compliance',
        description: 'Our privacy-compliant approach and documented explanation models make it easier for you to comply with current and upcoming laws (e.g., GDPR, AI Act).'
      },
      acceleratedInnovation: {
        title: 'Accelerated Innovation',
        description: 'Identify weaknesses and optimize AI models faster. Short iteration cycles ensure agile development - also thanks to our integrated feedback loops.'
      },
      riskMitigation: {
        title: 'Risk Mitigation',
        description: 'Reduce risks early by proactively uncovering potential biases or misinterpretations in your AI system.'
      },
      improvedUserAdoption: {
        title: 'Improved User Adoption',
        description: 'People are more likely to trust systems they understand. With our explainable AI solutions, acceptance and usage increase - internally and externally.'
      },
      betterBusinessOutcomes: {
        title: 'Better Business Outcomes',
        description: 'Make informed decisions about when a model is reliable and where human expertise is needed. This leads to better long-term results.'
      }
    },
    de: {
      title: 'Warum Explainable AI wählen?',
      subtitle: 'Unsere Plattform und Dienstleistungen bieten dir echten Mehrwert – von erhöhter Datensicherheit bis hin zu besserer Planbarkeit deines KI-Einsatzes.',
      enhancedTransparency: {
        title: 'Enhanced Decision Transparency',
        description: 'Verstehe den genauen Entscheidungsweg deiner KI-Modelle und baue Vertrauen bei Mitarbeitenden, Kunden und Regulierungsbehörden auf.'
      },
      regulatoryCompliance: {
        title: 'Regulatory Compliance',
        description: 'Unser datenschutzkonformer Ansatz und unsere dokumentierten Erklärmodelle erleichtern es dir, aktuelle und kommende Gesetze (z. B. DSGVO, AI Act) einzuhalten.'
      },
      acceleratedInnovation: {
        title: 'Accelerated Innovation',
        description: 'Identifiziere Schwachstellen und optimiere KI-Modelle schneller. Kurze Iterationszyklen sorgen für eine agile Entwicklung – auch dank unserer integrierten Feedback-Schleifen.'
      },
      riskMitigation: {
        title: 'Risk Mitigation',
        description: 'Reduziere Risiken frühzeitig, indem du mögliche Verzerrungen oder Fehlinterpretationen in deinem KI-System proaktiv aufdeckst und behebst.'
      },
      improvedUserAdoption: {
        title: 'Improved User Adoption',
        description: 'Menschen vertrauen eher Systemen, die sie verstehen. Mit unseren erklärbaren KI-Lösungen steigt Akzeptanz und Nutzung – intern wie extern.'
      },
      betterBusinessOutcomes: {
        title: 'Better Business Outcomes',
        description: 'Triff fundierte Entscheidungen, wann ein Modell verlässlich ist und wo menschliche Expertise gefragt ist. So lassen sich langfristig bessere Ergebnisse erzielen.'
      }
    }
  },
  
  // Contact
  contact: {
    en: {
      title: 'Contact Us',
      subtitle: 'Do you have questions about our AI solutions or would you like to learn more about our data protection-compliant offerings? Contact us directly via the form or use our contact details.',
      name: 'Name *',
      email: 'Email *',
      subject: 'Subject *',
      message: 'Message *',
      send: 'Send Message',
      sending: 'Sending...',
      successMessage: 'Thank you for your message! We will get back to you shortly.',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      addressLabel: 'Address'
    },
    de: {
      title: 'Kontaktieren Sie uns',
      subtitle: 'Haben Sie Fragen zu unseren KI-Lösungen oder möchten Sie mehr über unsere datenschutzkonformen Angebote erfahren? Kontaktieren Sie uns direkt über das Formular oder nutzen Sie unsere Kontaktdaten.',
      name: 'Name *',
      email: 'E-Mail *',
      subject: 'Betreff *',
      message: 'Nachricht *',
      send: 'Nachricht senden',
      sending: 'Wird gesendet...',
      successMessage: 'Vielen Dank für Ihre Nachricht! Wir werden uns in Kürze bei Ihnen melden.',
      emailLabel: 'E-Mail',
      phoneLabel: 'Telefon',
      addressLabel: 'Adresse'
    }
  },
  
  // Footer
  footer: {
    en: {
      description: 'Explainable AI – No more black box. We focus on transparency and data protection so companies can use AI safely and efficiently.',
      company: 'Company',
      aboutUs: 'About Us',
      ourTeam: 'Our Team',
      careers: 'Careers',
      contactUs: 'Contact Us',
      resources: 'Resources',
      blog: 'Blog',
      documentation: 'Documentation',
      caseStudies: 'Case Studies',
      helpCenter: 'Help Center',
      legal: 'Legal',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      cookiePolicy: 'Cookie Policy',
      dataProcessing: 'Data Processing',
      copyright: 'All rights reserved.',
      terms: 'Terms',
      privacy: 'Privacy',
      cookies: 'Cookies',
      privacyNotice: 'Privacy Notice'
    },
    de: {
      description: 'Explainable AI – Keine Blackbox mehr. Wir setzen auf Transparenz und Datenschutz, damit Unternehmen KI sicher und effizient nutzen können.',
      company: 'Unternehmen',
      aboutUs: 'Über uns',
      ourTeam: 'Unser Team',
      careers: 'Karriere',
      contactUs: 'Kontakt',
      resources: 'Ressourcen',
      blog: 'Blog',
      documentation: 'Dokumentation',
      caseStudies: 'Fallstudien',
      helpCenter: 'Hilfe-Center',
      legal: 'Rechtliches',
      termsOfService: 'Nutzungsbedingungen',
      privacyPolicy: 'Datenschutzerklärung',
      cookiePolicy: 'Cookie-Richtlinie',
      dataProcessing: 'Datenverarbeitung',
      copyright: 'Alle Rechte vorbehalten.',
      terms: 'AGB',
      privacy: 'Datenschutz',
      cookies: 'Cookies',
      privacyNotice: 'Datenschutzerklärung'
    }
  },
  
  // Language Switcher
  languageSwitcher: {
    en: {
      de: 'Deutsch',
      en: 'English'
    },
    de: {
      de: 'Deutsch',
      en: 'English'
    }
  }
};

export default translations;
